import clsx from "clsx";
import React, { Fragment } from "react";
import { compact, get, isNil } from "lodash";

// MUI
import { Box, Stack, Typography } from "@mui/material";
import { GridAlignment, GridColDef, GridColType, GridRenderCellParams, GridSingleSelectColDef } from "@mui/x-data-grid-premium";

// Types
import { AggregationFunction, AggregationModel, FnI_Status, FormattedBoolean, RO_Status } from "./types";
import { DateParams, MappingColumn, MappingColumnType, MappingColumnValue } from "@/Common/types/meta/mappingFilters";
import { IMailDetails } from "@DataPages/types/rci/salesLeads/model";

// Layouts
import TypographyCell from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";

// Components
import EmailsRenderCell from "@DataPages/components/common/EmailsRenderCell";
import PhoneNumbersRenderCell from "@DataPages/components/common/PhoneNumbersRenderCell";

// Icons
import { Circle } from "@mui/icons-material";

// Helpers
import { getDateInUTC, getFormattedDate } from "@dealerwave/dw-layer-date-converter";
import { formatNumber } from "@/Common/helpers/number";
import { getCellClassNameFn } from "@/Common/helpers/Tables";
import { getPinnedCellClassName, isAutoGeneratedFooter } from "@/Layouts/components/Tables/DataSmartTable/helpers";
import { formatPhoneNumber } from "@Common/helpers/Modifiers/formatters";

// Constants
import { HEADER_CLASS_NAME } from "@/Constants/tables";

export const SEPARATOR_COL_ID = "__empty";

export function makeGridColDefs(mappingCols: MappingColumn[]): GridColDef[] {
	const pinnedCols = mappingCols.filter((col) => col.isPinned);
	const notPinnedCols = mappingCols.filter((col) => !col.isPinned);

	const pinnedGridCols = compact(pinnedCols.map((mappingCol) => makeGridColDef(mappingCol)));
	const notPinnedGridCols = compact(notPinnedCols.map((mappingCol) => makeGridColDef(mappingCol)));

	const hasSeparator = pinnedGridCols.length > 0 && notPinnedGridCols.length > 0;
	return [...pinnedGridCols, ...(hasSeparator ? [makeGridSeparatorColDef()] : []), ...notPinnedGridCols];
}

export function getPinnedColumns(mappingCols: MappingColumn[]): string[] {
	return mappingCols
		.filter((col) => col.isPinned)
		.map(getColKey)
		.filter(Boolean);
}

export function getAggregatedColumns(mappingCols: MappingColumn[]): AggregationModel {
	return mappingCols.reduce((result: AggregationModel, col) => {
		if (col.columnType === "NUMBER" && col.variant === "MONEY") {
			result[getColKey(col)] = AggregationFunction.sum;
		}
		return result;
	}, {});
}

// MARK:- Columns definitions
function makeGridColDef(mappingCol: MappingColumn): GridColDef | null {
	const GridColDefMapBuilder = new Map<MappingColumnType, (mappingCol: MappingColumn) => GridColDef | null>([
		["STRING", makeStringColDef],
		["NUMBER", makeNumberColDef],
		["BOOLEAN", makeBooleanColDef],
		["DATE", makeDateColDef],
		["DATETIME", makeDateColDef],
		["CUSTOM", makeCustomColDef],
	]);

	const colDefBuilder = GridColDefMapBuilder.get(mappingCol.columnType);
	return colDefBuilder ? colDefBuilder(mappingCol) : null;
}

function makeCustomColDef(mappingCol: MappingColumn): GridColDef | null {
	const CustomColDefMapBuilder = new Map<string, (mappingCol: MappingColumn) => GridColDef>([
		["MODAL", makeModalColDef],
		["RO_STATUS", makeRoStatusColDef],
		["F&I_STATUS", makeFnIStatusColDef],
		["ARRAY", makeArrayColDef],
		["PHONE", makePhoneColDef],
		["EMAIL", makeEmailColDef],
	]);

	const customColBuilder = CustomColDefMapBuilder.get(mappingCol.variant ?? "");
	return customColBuilder ? customColBuilder(mappingCol) : null;
}

function getGridCommonPropsColDef(mappingCol: MappingColumn): GridColDef {
	const ColTypeMapper = new Map<MappingColumnType, GridColType>([
		["STRING", "string"],
		["NUMBER", "number"],
		["BOOLEAN", "singleSelect"],
		["DATE", "date"],
		["DATETIME", "dateTime"],
	]);

	return {
		field: getColKey(mappingCol),
		headerName: mappingCol.headerName?.includes("%br%") ? mappingCol.headerName?.split("%br%").join(" ") : mappingCol.headerName,
		width: mappingCol.width,
		type: ColTypeMapper.get(mappingCol.columnType),
		filterable: mappingCol?.filterable ?? true,
		resizable: mappingCol?.resizable,
		sortable: mappingCol?.sortable ?? true,
		flex: mappingCol?.flex,
		headerAlign: getColHeaderAlignment(mappingCol),
		align: getColAlignment(mappingCol),
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: mappingCol.isPinned ? getPinnedCellClassName : getCellClassNameFn(),
		valueGetter: (_, row) => get(row, pathToValue(mappingCol)),
		renderHeader: (params) => {
			if (mappingCol?.headerName?.includes("%br%")) {
				return (
					<TypographyHeader params={params} right={mappingCol?.headerAlignment === "right"}>
						{generateMultiLineHeader(mappingCol.headerName)}
					</TypographyHeader>
				);
			}

			return <TypographyHeader params={params} />;
		},
	};
}

function generateMultiLineHeader(text: string) {
	const divider = "%br%";
	const segments = text.split(divider);
	return (
		<Box>
			{segments.map((segment, idx) => (
				<Fragment key={idx}>
					{segment}
					{idx < segments.length - 1 && <br />}
				</Fragment>
			))}
		</Box>
	);
}

function getColHeaderAlignment(mappingCol: MappingColumn): GridAlignment | undefined {
	return mappingCol?.headerAlignment;
}

function getColAlignment(mappingCol: MappingColumn): GridAlignment | undefined {
	return mappingCol?.align;
}

function makeGridSeparatorColDef(): GridColDef {
	return {
		field: SEPARATOR_COL_ID,
		headerName: "",
		sortable: false,
		resizable: false,
		filterable: false,
		width: 12,
		headerClassName: "!z-10 !bg-white !border-none !p-0",
		cellClassName: () => "empty-row !bg-white !border-none !p-0",
		renderCell: () => <TypographyCell className={"!bg-blue"} />,
	};
}

function makeStringColDef(mappingCol: MappingColumn): GridColDef {
	return {
		...getGridCommonPropsColDef(mappingCol),
		valueGetter: (_, row) => getValueForStringCol(mappingCol, row),
		renderCell: (params) => renderCellForString({ params, columnOrder: mappingCol.order }),
	};
}

function makeNumberColDef(mappingCol: MappingColumn): GridColDef {
	return {
		...getGridCommonPropsColDef(mappingCol),
		renderHeader: (params) => (
			<TypographyHeader
				params={params}
				left={mappingCol?.headerAlignment === "left"}
				right={mappingCol?.headerAlignment === "right"}
			/>
		),
		renderCell: (params) => renderCellForNumber({ params, mappingCol }),
	};
}

function makeBooleanColDef(mappingCol: MappingColumn): GridSingleSelectColDef {
	return {
		...getGridCommonPropsColDef(mappingCol),
		type: "singleSelect",
		valueOptions: FormattedBoolean.allValues,
		valueGetter: (_, row) => (get(row, pathToValue(mappingCol)) ? FormattedBoolean.Y : FormattedBoolean.N),
		renderCell: (params) => renderStandardCell(params, mappingCol),
	};
}

function makeDateColDef(mappingCol: MappingColumn): GridColDef {
	const { format, timezone } = mappingCol?.dateParams ?? {};
	return {
		...getGridCommonPropsColDef(mappingCol),
		valueGetter: (_, row) => getValueForDateCol(mappingCol, row, mappingCol?.dateParams),
		valueFormatter: (value) => getFormattedDate(value, timezone ?? "", format),
		renderCell: (params) => renderStandardCell(params, mappingCol),
	};
}

function makeModalColDef(mappingCol: MappingColumn): GridColDef {
	return {
		...getGridCommonPropsColDef(mappingCol),
		type: "string",
		sortable: false,
		filterable: false,
		renderCell: (params) => renderCellForCustomModal({ params, mappingCol }),
	};
}

function makeRoStatusColDef(mappingCol: MappingColumn): GridSingleSelectColDef {
	return {
		...getGridCommonPropsColDef(mappingCol),
		type: "singleSelect",
		valueOptions: RO_Status.allValues,
		renderCell: renderCellForRoStatus,
	};
}

function makeFnIStatusColDef(mappingCol: MappingColumn): GridSingleSelectColDef {
	return {
		...getGridCommonPropsColDef(mappingCol),
		type: "singleSelect",
		valueOptions: FnI_Status.allValues,
		renderCell: renderCellForFnIStatus,
	};
}

function makeArrayColDef(mappingCol: MappingColumn): GridColDef {
	return {
		...getGridCommonPropsColDef(mappingCol),
		type: "string",
		sortable: false,
		filterable: false,
		renderCell: (params) => renderCellForArray({ params, mappingCol }),
	};
}

function makePhoneColDef(mappingCol: MappingColumn): GridColDef {
	return {
		...getGridCommonPropsColDef(mappingCol),
		type: "string",
		sortable: false,
		filterable: mappingCol.filterable ?? true,
		valueGetter: (_, row) => {
			const value = get(row, pathToValue(mappingCol));
			if (!Array.isArray(value)) {
				return "";
			}
			return value.map((phone) => {
				const type = phone?.type ? `(${phone?.type})` : "";
				const formattedPhone = formatPhoneNumber(phone?.number, false);
				const ext = phone?.extension ? `ext. ${phone?.extension}` : "";
				return `${type} ${formattedPhone} ${ext}`;
			});
		},
		renderCell: (params) => <PhoneNumbersRenderCell params={params} />,
	};
}

function makeEmailColDef(mappingCol: MappingColumn): GridColDef {
	return {
		...getGridCommonPropsColDef(mappingCol),
		type: "string",
		sortable: false,
		filterable: mappingCol.filterable ?? true,
		valueGetter: (_, row) => {
			const value = get(row, pathToValue(mappingCol));
			if (!Array.isArray(value)) {
				return "";
			}
			return (value as IMailDetails[]).map((emailItem) => {
				return emailItem?.mailTo?.toLocaleLowerCase();
			});
		},
		renderCell: (params) => <EmailsRenderCell params={params} />,
	};
}

// MARK:- Value getters
function getValueForStringCol(mappingCol: MappingColumn, row: Record<string, any>): string {
	const values = mappingCol.pathToValues?.map((path) => get(row, path.pathToValue)) ?? [];
	const separator = mappingCol.separator ?? " ";
	return compact(values).join(separator);
}

function getValueForDateCol(mappingCol: MappingColumn, row: Record<string, any>, dateParams?: DateParams): Date | null {
	const value = get(row, pathToValue(mappingCol));
	if (!value) {
		return null;
	}
	const { isUTC } = dateParams ?? {};
	return isUTC ? getDateInUTC(value) : new Date(value);
}

function getValueForModal(mappingCol: MappingColumn, row: Record<string, any>): MappingColumnValue[] {
	return mappingCol.pathToValues?.map((path) => ({ title: path.alternativeText ?? "", value: get(row, path.pathToValue) })) ?? [];
}

// MARK:- Renderers
function renderCellForString(args: { params: GridRenderCellParams; columnOrder: number }): React.ReactNode {
	const { params, columnOrder } = args;
	const isFirstAggregatedColumn = columnOrder === 1 && isAutoGeneratedFooter(params.id);
	return isFirstAggregatedColumn ? "Totals" : <TypographyCell params={params} />;
}

function renderCellForNumber(args: { params: GridRenderCellParams; mappingCol: MappingColumn }): React.ReactNode {
	const { params, mappingCol } = args;
	const { value } = params;
	const { variant, format } = mappingCol;

	const isMoney = variant === "MONEY";
	if ((isAutoGeneratedFooter(params.id) && !isMoney) || isNil(value)) {
		return "";
	}

	const variantHandlers = new Map<string, (formattedValue: string) => React.ReactNode>([
		[
			"MONEY",
			() => (
				<TypographyCell
					params={params}
					render={"account"}
					align={"right"}
					formatOptions={{ decimals: 2 }}
					hints={{ isMoney, noFocus: true }}
				/>
			),
		],
		[
			"PERCENTAGE",
			(formattedValue) => {
				return (
					<TypographyCell params={params} render={"default"} align={"right"} hints={{ isPercentage: true, noFocus: true }}>
						{formattedValue}
					</TypographyCell>
				);
			},
		],
	]);

	const variantHandler = variantHandlers.get(variant ?? "");
	if (variantHandler) {
		const formattedNumber = Number(value).toFixed(2);
		const displayNumber = formattedNumber === "0.00" && variant === "MONEY" ? "-" : formattedNumber;
		return variantHandler(displayNumber);
	}
	const maximumFractionDigits = parseInt(format ?? "0", 10);
	const formattedValue = formatNumber(undefined, { maximumFractionDigits }, value);
	return <TypographyCell params={params}>{formattedValue}</TypographyCell>;
}

function renderStandardCell(params: GridRenderCellParams, mappingColumn?: MappingColumn): React.ReactNode {
	if (isAutoGeneratedFooter(params.id) && mappingColumn?.footer?.label) {
		return mappingColumn.footer.label;
	}

	return isAutoGeneratedFooter(params.id) ? "" : <TypographyCell params={params} />;
}

function renderCellForCustomModal(args: { params: GridRenderCellParams; mappingCol: MappingColumn }) {
	const { mappingCol, params } = args;

	if (isAutoGeneratedFooter(params.id)) {
		return "";
	}

	const valuesForModal = getValueForModal(mappingCol, params.row);
	const openModal = () => mappingCol.modalClickHandler?.({ data: valuesForModal });
	return makeHyperlinkStyledCell({ text: "Show value", onClick: openModal });
}

function makeHyperlinkStyledCell(args: { text: string; onClick: () => void }): React.ReactNode {
	const { text, onClick } = args;

	return (
		<Typography
			className="!text-primary !font-sans underline decoration-primary cursor-pointer !text-[12px] pt-[3px]"
			onClick={onClick}
		>
			{text}
		</Typography>
	);
}

function renderCellForRoStatus(params: GridRenderCellParams): React.ReactNode {
	return isAutoGeneratedFooter(params.id) ? "" : makeCellForRoStatus(params);
}

function makeCellForRoStatus(params: GridRenderCellParams): React.ReactNode {
	const value = params.value as string;
	const color = RO_Status.getColor(value);
	const fillClass = color ? `!text-${color}` : "";

	return (
		<Stack direction="row" spacing={1}>
			<Circle className={clsx("!w-[10px]", fillClass)} />
			<TypographyCell params={params} />
		</Stack>
	);
}

function renderCellForFnIStatus(params: GridRenderCellParams): React.ReactNode {
	return isAutoGeneratedFooter(params.id) ? "" : makeCellForFnIStatus(params);
}

function makeCellForFnIStatus(params: GridRenderCellParams): React.ReactNode {
	const value = params.value as string;
	const color = FnI_Status.getFIClosedStatusColor(value);
	const fillClass = color ? `!text-${color}` : "";

	return (
		<Stack direction="row" spacing={1}>
			<Circle className={clsx("!w-[10px]", fillClass)} />
			<TypographyCell params={params} />
		</Stack>
	);
}

function renderCellForArray(args: { params: GridRenderCellParams; mappingCol: MappingColumn }) {
	const { mappingCol, params } = args;

	if (isAutoGeneratedFooter(params.id)) {
		return "";
	}

	const content = params.value;
	const hasContent = (Array.isArray(content) && content.length > 0) || Object.keys(content ?? {}).length > 0;

	if (!hasContent) {
		return null;
	}

	const title = mappingCol.pathToValues?.[0].dialogTitle ?? mappingCol.headerName;
	const openModal = () => mappingCol.modalClickHandler?.({ title, data: content });
	const cellText = mappingCol.pathToValues?.[0].alternativeText ?? "Show value";

	return makeHyperlinkStyledCell({ text: cellText, onClick: openModal });
}

// MARK:- Supporting functions
function pathToValue(mappingCol: MappingColumn): string {
	return mappingCol.pathToValues?.[0].pathToValue ?? "";
}

function getColKey(mappingCol: MappingColumn): string {
	return `${pathToValue(mappingCol)}_${mappingCol.headerKey}`;
}
